import { createElement, type ReactElement, type ReactNode } from 'react'
import { TextLink } from '@/components/TextLink/TextLink'
import { type StoryblokAsset } from '@/services/storyblok/storyblok'
import { getLinkRel } from '@/services/storyblok/Storyblok.helpers'
import { StoryblokComponent } from '@storyblok/react'
import { BlockTypes, type LinkNode, MarkTypes, richTextResolver, type StoryblokRichTextNode } from '@storyblok/richtext'
import { ImageBlockRenderer } from '../ImageBlock/ImageBlockRenderer'
import { convertAttributesInElement, fieldHasValidContent } from './richTextUtils'
import {
  richTextDivider,
  richTextHeading,
  richTextList,
  richTextListItem,
  richTextParagraph,
} from '@/components/RichText/RichText.css'
import { textLink } from '@/components/TextLink/TextLink.css'

export const renderRichTextToJsx = (content: StoryblokRichTextNode): ReactNode => {
  if (!fieldHasValidContent(content)) {
    return null
  }

  const html = resolver.render(content as unknown as StoryblokRichTextNode<ReactElement>)
  const formattedHtml = convertAttributesInElement(html)
  return formattedHtml
}

export const renderRichTextToString = (content: StoryblokRichTextNode): string =>
  richTextResolver<string>().render(content)

const resolver = richTextResolver({
  renderFn: createElement,
  keyedResolvers: true,
  resolvers: {
    // @ts-expect-error type mismatch on linktype property, no idea how to fix this cheaply
    [MarkTypes.LINK]: (node: LinkNode<ReactElement>) => {
      let href = ''
      if (node.attrs.href) {
        href = node.attrs.href
      } else {
        console.warn(
          "[RichTextBlock]: No 'href' provided to link. This is probably a configuration issue. Using '' as placeholder",
        )
      }
      const linkProps = {
        href: appendAnchor(href, node.attrs.anchor),
        children: node.text,
        target: node.attrs.target,
        ...node.attrs.custom,
      }

      if (node.attrs.linktype === 'email') {
        linkProps.href = `mailto:${href}`
        return <a key={node.attrs.uuid} {...linkProps} className={textLink} />
      } else if (isExternalLink(href)) {
        // External links
        const rel = getLinkRel(linkProps)
        return <a key={node.attrs.uuid} {...linkProps} rel={rel} className={textLink} />
      } else {
        // Internal links
        return <TextLink as="span" size="inherit" key={node.attrs.uuid} {...linkProps} />
      }
    },
    [BlockTypes.HEADING]: (node: StoryblokRichTextNode<ReactElement>, ctx) => {
      // Default to h3 if level would be undefined (should not happen)
      const level = node.attrs?.level ?? 3
      return ctx.render(`h${level}`, { class: richTextHeading }, node.children)
    },
    [BlockTypes.PARAGRAPH]: (node: StoryblokRichTextNode<ReactElement>, ctx) => {
      return ctx.render('p', { class: richTextParagraph }, node.children)
    },
    [BlockTypes.OL_LIST]: (node: StoryblokRichTextNode<ReactElement>, ctx) => {
      return ctx.render('ol', { class: richTextList.ol }, node.children)
    },
    [BlockTypes.UL_LIST]: (node: StoryblokRichTextNode<ReactElement>, ctx) => {
      return ctx.render('ul', { class: richTextList.ul }, node.children)
    },
    [BlockTypes.LIST_ITEM]: (node: StoryblokRichTextNode<ReactElement>, ctx) => {
      return ctx.render('li', { class: richTextListItem }, node.children)
    },
    [BlockTypes.HR]: (_, ctx) => {
      return ctx.render('hr', { class: richTextDivider })
    },
    [MarkTypes.STYLED]: (node) => {
      return (
        <span key={node.text} className={node.attrs?.class}>
          {node.text}
        </span>
      )
    },
    [MarkTypes.ANCHOR]: (node): ReactElement => {
      const id = node.attrs?.id
      if (id == null) {
        // Should not happen, but let's play it safe
        return null as unknown as ReactElement
      }
      return (
        <span key={`anchor-${id}`} id={id}>
          {node.text}
        </span>
      )
    },
    [BlockTypes.IMAGE]: (node: StoryblokRichTextNode<ReactElement>) => {
      const attrs = node.attrs!
      const blok = {
        image: {
          alt: attrs.alt,
          filename: attrs.src,
        } as StoryblokAsset,
      }
      return <ImageBlockRenderer key={`image-${attrs.id}`} blok={blok} nested={true} />
    },
    [BlockTypes.COMPONENT]: componentResolver,
  },
  // No need to escape anything, React does this already
  textFn: (text: string) => text as unknown as ReactElement<unknown, string>,
})

function componentResolver(node: StoryblokRichTextNode<ReactElement>) {
  // Convert this to use React.createElement or JSX
  // Example with JSX:
  return createElement(StoryblokComponent, { blok: node.attrs?.body[0], id: node.attrs?.id })
}

const isExternalLink = (href?: string) => href?.match(/^(https?:)?\/\//)

const appendAnchor = (href: string, anchor?: string) => (anchor ? `${href}#${anchor}` : href)
